/**
 * ContentHubLayout Component
 *
 * Main container for Content Hub with tab navigation for Drafts, Posted, and Rejected.
 * Enhanced with skeleton loaders, empty states, search/filtering, and animations.
 */

import { useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import { AnimatePresence } from "framer-motion";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Content Hub components
import PostCard from "components/ContentHub/PostCard";
import PostedCard from "components/ContentHub/PostedCard";
import RSSContentCard from "components/ContentHub/RSSContentCard";
import ReviewModal from "components/ContentHub/ReviewModal";
import PostedModal from "components/ContentHub/PostedModal";
import RSSContentModal from "components/ContentHub/RSSContentModal";
import EditModal from "components/ContentHub/EditModal";
import SuccessCard from "components/ContentHub/SuccessCard";
import CreatePostModal from "components/ContentHub/CreatePostModal";

// New UI Enhancement components
import { GridSkeleton } from "components/ContentHub/Skeletons";
import EmptyState from "components/ContentHub/EmptyState";
import SearchFilterBar, { useContentFilter } from "components/ContentHub/SearchFilterBar";
import AnimatedCard from "components/ContentHub/AnimatedCard";
import SuccessCelebration from "components/ContentHub/SuccessCelebration";
import TabWithBadge from "components/ContentHub/TabWithBadge";

// Hooks
import usePosts from "hooks/usePosts";
import useRSSContent from "hooks/useRSSContent";
import useApproval from "hooks/useApproval";
import useDraftGeneration from "hooks/useDraftGeneration";
import useEdit from "hooks/useEdit";
import useCreatePost from "hooks/useCreatePost";

// Tab count hooks - fetch counts for all statuses
import { useState as useTabCounts } from "react";

function ContentHubLayout() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedRSSItem, setSelectedRSSItem] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isRSSModalOpen, setIsRSSModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showSuccessCard, setShowSuccessCard] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("today");

  // Success celebration state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState({ title: "", message: "" });

  // Success toast state
  const [successToast, setSuccessToast] = useState({
    show: false,
    message: "",
    title: "",
  });

  // Determine status based on active tab
  const getStatusForTab = (tab) => {
    switch (tab) {
      case 0:
        return "New"; // RSS Content - raw feed items
      case 1:
        return "Pending Approval"; // Drafts - AI-generated posts ready for review
      case 2:
        return "Posted"; // Published posts
      case 3:
        return "Rejected"; // Rejected posts
      default:
        return "Pending Approval";
    }
  };

  const currentStatus = getStatusForTab(activeTab);
  const isRSSTab = activeTab === 0;

  // Fetch RSS content for Tab 0
  const {
    items: rssItems,
    loading: rssLoading,
    error: rssError,
    refetch: refetchRSS,
  } = useRSSContent("New");

  // Fetch blog posts for Tabs 1, 2, 3
  const { posts, loading: postsLoading, error: postsError, refetch } = usePosts(currentStatus);

  // Apply client-side filtering to content
  const filteredRSSItems = useContentFilter(rssItems, searchQuery, activeFilter);
  const filteredPosts = useContentFilter(posts, searchQuery, activeFilter);

  // Helper function to show success toast
  const showSuccessToast = (title, message) => {
    setSuccessToast({
      show: true,
      title,
      message,
    });
  };

  // Draft generation hook with success callback
  const {
    selectForDraft,
    isProcessing,
    error: draftError,
    success: draftSuccess,
    resetState: resetDraftState,
  } = useDraftGeneration((rssContentId) => {
    showSuccessToast(
      "Draft Generated!",
      "Your content has been successfully converted to a draft. Check the Drafts tab."
    );
    refetchRSS(); // Refresh RSS content
    setTimeout(() => setActiveTab(1), 1500); // Auto-switch to Drafts tab after toast
  });

  // Approval hook
  const {
    approvePost,
    rejectPost,
    loading: approvalLoading,
    loadingStage,
    success: approvalSuccess,
    error: approvalError,
    successData,
    resetState: resetApprovalState,
  } = useApproval();

  // Edit hook
  const {
    editWithAI,
    manualEdit,
    loading: editLoading,
    loadingStage: editLoadingStage,
    success: editSuccess,
    error: editError,
    resetState: resetEditState,
  } = useEdit();

  // Create post hook with success callback
  const {
    createPost,
    loading: createLoading,
    error: createError,
    success: createSuccess,
    resetState: resetCreateState,
  } = useCreatePost(() => {
    showSuccessToast(
      "Draft Created!",
      "Your new post has been successfully created. Check the Drafts tab."
    );
    refetch(); // Refresh the posts list
    setTimeout(() => setActiveTab(1), 1500); // Auto-switch to Drafts tab after toast
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedPost(null);
    setSelectedRSSItem(null);
    setIsReviewModalOpen(false);
    setIsRSSModalOpen(false);
    // Reset search/filter when changing tabs
    setSearchQuery("");
    // Default to "today" for Content tab, "all" for others
    setActiveFilter(newValue === 0 ? "today" : "all");
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setIsReviewModalOpen(true);
  };

  const handleRSSItemClick = (item) => {
    setSelectedRSSItem(item);
    setIsRSSModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!approvalLoading) {
      setIsReviewModalOpen(false);
      setSelectedPost(null);
      resetApprovalState();
    }
  };

  const handleCloseRSSModal = () => {
    // Allow closing if not processing, or if processing but user wants to close
    const currentlyProcessing = selectedRSSItem ? isProcessing(selectedRSSItem.id) : false;
    if (!currentlyProcessing) {
      setIsRSSModalOpen(false);
      setSelectedRSSItem(null);
      resetDraftState();
    }
  };

  const handleSelectForDraft = async (item) => {
    const result = await selectForDraft(item);
    if (result.success) {
      // Success handled by callback in useDraftGeneration hook
      // Keep modal open during processing to show status
    }
  };

  const handleApprove = async (post) => {
    const result = await approvePost(post);
    if (result.success) {
      // Show celebration animation
      setCelebrationData({
        title: "Post Published! 🎉",
        message: "Your content has been successfully published to social media platforms.",
      });
      setShowCelebration(true);
      setShowSuccessCard(true);
      setIsReviewModalOpen(false);
      refetch(); // Refresh the list
      setTimeout(() => setActiveTab(2), 2000); // Auto-switch to Posted tab
    }
  };

  const handleReject = async (postId) => {
    const result = await rejectPost(postId);
    if (result.success) {
      showSuccessToast("Post Rejected", "The post has been moved to the rejected section.");
      setIsReviewModalOpen(false);
      setSelectedPost(null);
      resetApprovalState();
      refetch(); // Refresh the list
      setTimeout(() => setActiveTab(3), 1500); // Auto-switch to Rejected tab
    }
  };

  const handleEdit = (post) => {
    // Close review modal and open edit modal
    setIsReviewModalOpen(false);
    setSelectedPost(post);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    if (!editLoading) {
      setIsEditModalOpen(false);
      resetEditState();
    }
  };

  const handleAIRewrite = async (postId, editData) => {
    const result = await editWithAI(postId, editData);
    if (result.success) {
      showSuccessToast(
        "Content Rewritten!",
        "The AI has successfully rewritten your content. Please review the updated post."
      );
      setIsEditModalOpen(false);
      setSelectedPost(null);
      resetEditState();
      refetch(); // Refresh the list
      setTimeout(() => setActiveTab(1), 1500); // Auto-switch back to Drafts tab
    }
  };

  const handleManualEdit = async (postId, editedContent) => {
    const result = await manualEdit(postId, editedContent);
    if (result.success) {
      showSuccessToast("Changes Saved!", "Your edits have been saved successfully.");
      setIsEditModalOpen(false);
      setSelectedPost(null);
      resetEditState();
      refetch(); // Refresh the list
    }
  };

  const handleDismissSuccess = () => {
    setShowSuccessCard(false);
    setSelectedPost(null);
    resetApprovalState();
  };

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    if (!createLoading) {
      setIsCreateModalOpen(false);
      resetCreateState();
    }
  };

  const handleCreatePost = async (creationData) => {
    const result = await createPost(creationData);
    if (result.success) {
      // Success handled by callback in useCreatePost hook
      // Keep modal open during processing to show status
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* Make the main content area scrollable, not the sidebar */}
      <MDBox
        py={3}
        sx={{
          height: "calc(100vh - 64px)", // adjust 64px if your navbar/footer height differs
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Header - Enhanced with better visual hierarchy */}
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <MDBox>
            <MDBox display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <MDBox
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 14px rgba(26, 115, 232, 0.3)",
                }}
              >
                <Icon sx={{ color: "white", fontSize: "1.3rem !important" }}>hub</Icon>
              </MDBox>
              <MDTypography
                variant="h3"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                  color: "#1a1a2e",
                }}
              >
                Content Hub
              </MDTypography>
            </MDBox>
            <MDTypography
              variant="body2"
              sx={{
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                color: "#6B7280",
                pl: { xs: 0, sm: 7 },
              }}
            >
              Manage, review, and publish your content across platforms
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<Icon>auto_awesome</Icon>}
            onClick={handleOpenCreateModal}
            sx={{
              minHeight: { xs: "48px", sm: "44px" },
              px: { xs: 2.5, sm: 3 },
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 4px 14px rgba(26, 115, 232, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 20px rgba(26, 115, 232, 0.4)",
              },
            }}
          >
            Create Post
          </MDButton>
        </MDBox>

        {/* Tabs with Count Badges */}
        <MDBox
          mb={3}
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
            overflow: { xs: "auto", sm: "visible" },
            // Removed WebkitOverflowScrolling for desktop compatibility
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                minHeight: { xs: "56px", sm: "64px" },
                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                textTransform: "none",
                fontWeight: 500,
                transition: "all 0.2s ease",
                px: { xs: 2, sm: 3 },
              },
              "& .Mui-selected": {
                fontWeight: 700,
                color: "#1A73E8 !important",
              },
              "& .MuiTabs-indicator": {
                height: "3px",
                borderRadius: "3px 3px 0 0",
                backgroundColor: "#1A73E8",
              },
            }}
          >
            <Tab
              label={
                <TabWithBadge
                  icon="rss_feed"
                  label="Content"
                  count={rssItems.length}
                  color="info"
                  isActive={activeTab === 0}
                />
              }
            />
            <Tab
              label={
                <TabWithBadge
                  icon="edit_note"
                  label="Drafts"
                  count={activeTab === 1 ? posts.length : 0}
                  color="warning"
                  isActive={activeTab === 1}
                  showBadge={activeTab === 1}
                />
              }
            />
            <Tab
              label={
                <TabWithBadge
                  icon="check_circle"
                  label="Posted"
                  count={activeTab === 2 ? posts.length : 0}
                  color="success"
                  isActive={activeTab === 2}
                  showBadge={activeTab === 2}
                />
              }
            />
            <Tab
              label={
                <TabWithBadge
                  icon="do_not_disturb"
                  label="Rejected"
                  count={activeTab === 3 ? posts.length : 0}
                  color="error"
                  isActive={activeTab === 3}
                  showBadge={activeTab === 3}
                />
              }
            />
          </Tabs>
        </MDBox>

        {/* Error Alert */}
        {((isRSSTab && rssError) || (!isRSSTab && postsError) || approvalError || draftError) && (
          <MDBox mb={3}>
            <MDAlert color="error" dismissible>
              {rssError || postsError || approvalError || draftError}
            </MDAlert>
          </MDBox>
        )}

        {/* Search and Filter Bar */}
        {((isRSSTab && rssItems.length > 0) || (!isRSSTab && posts.length > 0)) && (
          <SearchFilterBar
            tab={
              isRSSTab
                ? "rss"
                : activeTab === 1
                ? "drafts"
                : activeTab === 2
                ? "posted"
                : "rejected"
            }
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            resultCount={isRSSTab ? filteredRSSItems.length : filteredPosts.length}
            totalCount={isRSSTab ? rssItems.length : posts.length}
          />
        )}

        {/* Loading State - Skeleton Loaders */}
        {((isRSSTab && rssLoading && rssItems.length === 0) ||
          (!isRSSTab && postsLoading && posts.length === 0)) && (
          <GridSkeleton count={6} hasImage={!isRSSTab} hasButton={isRSSTab} />
        )}

        {/* Empty State - No content at all */}
        {((isRSSTab && !rssLoading && rssItems.length === 0 && !rssError) ||
          (!isRSSTab && !postsLoading && posts.length === 0 && !postsError)) && (
          <EmptyState
            variant={
              isRSSTab
                ? "rss"
                : activeTab === 1
                ? "drafts"
                : activeTab === 2
                ? "posted"
                : "rejected"
            }
            onAction={
              activeTab === 0
                ? refetchRSS
                : activeTab === 1
                ? handleOpenCreateModal
                : activeTab === 2
                ? () => setActiveTab(1)
                : null
            }
          />
        )}

        {/* Empty State - No search results */}
        {((isRSSTab && rssItems.length > 0 && filteredRSSItems.length === 0) ||
          (!isRSSTab && posts.length > 0 && filteredPosts.length === 0)) && (
          <EmptyState
            variant="search"
            onAction={() => {
              setSearchQuery("");
              setActiveFilter("all");
            }}
          />
        )}

        {/* Content Grid (Tab 0) */}
        {isRSSTab && filteredRSSItems.length > 0 && (
          <Grid container spacing={3}>
            <AnimatePresence mode="popLayout">
              {filteredRSSItems.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <AnimatedCard index={index}>
                    <RSSContentCard
                      item={item}
                      onSelect={handleSelectForDraft}
                      onClick={handleRSSItemClick}
                      isProcessing={isProcessing(item.id) || item.status === "Processing"}
                    />
                  </AnimatedCard>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}

        {/* Posts Grid (Tabs 1, 2, 3) */}
        {!isRSSTab && filteredPosts.length > 0 && (
          <Grid container spacing={3}>
            <AnimatePresence mode="popLayout">
              {filteredPosts.map((post, index) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <AnimatedCard index={index}>
                    {activeTab === 2 ? (
                      <PostedCard post={post} onClick={handlePostClick} />
                    ) : (
                      <PostCard post={post} onClick={handlePostClick} />
                    )}
                  </AnimatedCard>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}
      </MDBox>

      <Footer />

      {/* Success Celebration Overlay */}
      <SuccessCelebration
        show={showCelebration}
        title={celebrationData.title}
        message={celebrationData.message}
        onDismiss={() => setShowCelebration(false)}
        autoHide={true}
        autoHideDelay={3000}
      />

      {/* Content Modal */}
      <RSSContentModal
        open={isRSSModalOpen}
        item={selectedRSSItem}
        onClose={handleCloseRSSModal}
        onSelect={handleSelectForDraft}
        isProcessing={selectedRSSItem ? isProcessing(selectedRSSItem.id) : false}
      />

      {/* Review Modal for Drafts/Rejected Posts */}
      {(activeTab === 1 || activeTab === 3) && (
        <ReviewModal
          open={isReviewModalOpen}
          post={selectedPost}
          onClose={handleCloseModal}
          onApprove={handleApprove}
          onReject={handleReject}
          onEdit={handleEdit}
          loading={approvalLoading}
          loadingStage={loadingStage}
        />
      )}

      {/* Posted Modal for Posted Posts */}
      {activeTab === 2 && (
        <PostedModal open={isReviewModalOpen} post={selectedPost} onClose={handleCloseModal} />
      )}

      {/* Edit Modal */}
      <EditModal
        open={isEditModalOpen}
        post={selectedPost}
        onClose={handleCloseEditModal}
        onAIRewrite={handleAIRewrite}
        onManualEdit={handleManualEdit}
        loading={editLoading}
        loadingStage={editLoadingStage}
      />

      {/* Success Card */}
      {showSuccessCard && approvalSuccess && (
        <SuccessCard successData={successData} onDismiss={handleDismissSuccess} />
      )}

      {/* Create Post Modal */}
      <CreatePostModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreatePost}
        loading={createLoading}
        error={createError}
      />

      {/* Success Toast */}
      {successToast.show && (
        <MDSnackbar
          color="success"
          icon="check"
          title={successToast.title}
          content={successToast.message}
          dateTime="now"
          open={successToast.show}
          onClose={() => setSuccessToast({ show: false, message: "", title: "" })}
          close={() => setSuccessToast({ show: false, message: "", title: "" })}
          autoHideDuration={4000}
        />
      )}
    </DashboardLayout>
  );
}

export default ContentHubLayout;
